<script lang="ts" setup>
import { SearchParams, Studio, StudioUser } from '@/modules/Studios/types'
import { addStudioAdmin, removeStudioAdmin } from '@/enitites/studio/api/endpoints'
import { ref, watch } from 'vue'
import UserChip from '@/shared/ui/chips/UserChip.vue'
import BKInput from '@/components/AppShared/BKInput.vue'
import { mdiMagnify, mdiMinusCircleOutline, mdiPlus } from '@mdi/js'
import { BKModal } from '@/shared/ui/modals'
import BKUserSelect from '@/components/BKUserSelect.vue'
import Bugsnag from '@bugsnag/js'

const props = defineProps<{ studio: Studio }>()

const studioAdmins = ref<StudioUser[]>([])
const infiniteId = ref(Number(new Date()))
const showNotFound = ref(false)
const searchParams = ref<SearchParams>({
  pageNumber: 0,
  pageSize: 100,
  keyword: '',
  sortBy: 'name',
  sortDir: 'asc',
})

// Initialize studioAdmins with the admins from props.studio.admins
const initializeAdmins = () => {
  studioAdmins.value = [...props.studio.admins]
}

// Call initializeAdmins when the component is created
initializeAdmins()

const infiniteHandler = async ($state: { complete: () => void; loaded: () => void }) => {
  // Filter admins based on search keyword
  studioAdmins.value = props.studio.admins.filter((admin) =>
    admin.dancerName.toLowerCase().includes(searchParams.value.keyword?.toLowerCase() || '')
  )

  // Always complete since we're not doing pagination
  $state.complete()
}

const searchTimeout = ref<number | null>(null)

const showAddModal = ref(false)
const isAddingAdmin = ref(false)
const newlyAddedAdmins = ref<StudioUser[]>([])

const handleAddUser = async (user: StudioUser) => {
  isAddingAdmin.value = true

  try {
    const res = await addStudioAdmin(props.studio.id, user.id)
    if (!res) return

    // Update both studioAdmins and props.studio.admins
    studioAdmins.value.push(user)
    props.studio.admins.push(user)
    newlyAddedAdmins.value.push(user)
  } catch (e) {
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)), (event) => {
      event.addMetadata('studio', {
        context: 'Error adding studio admin',
        studioId: props.studio.id,
        userId: user.id,
      })
    })
  } finally {
    isAddingAdmin.value = false
  }
}

const handleDeleteUser = async (user: StudioUser) => {
  const res = await removeStudioAdmin(props.studio.id, user.id)
  if (res !== 'ok') return

  // Update both studioAdmins and props.studio.admins
  studioAdmins.value = studioAdmins.value.filter((u) => u.id !== user.id)
  props.studio.admins = props.studio.admins.filter((u) => u.id !== user.id)
  newlyAddedAdmins.value = newlyAddedAdmins.value.filter((u) => u.id !== user.id)
}

watch(
  () => searchParams.value.keyword,
  () => {
    if (searchTimeout.value) clearTimeout(searchTimeout.value)
    searchTimeout.value = setTimeout(() => {
      // Filter admins based on search keyword
      const filteredAdmins = props.studio.admins.filter((admin) =>
        admin.dancerName.toLowerCase().includes(searchParams.value.keyword?.toLowerCase() || '')
      )

      studioAdmins.value = filteredAdmins
      showNotFound.value = !filteredAdmins.length
      infiniteId.value += 1
      searchTimeout.value = null
    }, 600)
  }
)
</script>

<template>
  <div class="admins">
    <span class="editor-title">Admins</span>

    <div class="search">
      <BKInput
        v-model="searchParams.keyword"
        :append-icon="mdiMagnify"
        label="Search current admins"
        autocomplete="off"
        background-color="white"
        hide-details
        outlined
        single-line
      />
    </div>

    <div class="list">
      <UserChip
        v-for="user in studioAdmins"
        :key="JSON.stringify(user)"
        :user="user"
      >
        <template #actions>
          <v-icon
            class="close-icon"
            color="red"
            @click="() => handleDeleteUser(user)"
          >
            {{ mdiMinusCircleOutline }}
          </v-icon>
        </template>
      </UserChip>

      <infinite-loading
        :distance="400"
        :identifier="infiniteId"
        spinner="bubbles"
        @infinite="infiniteHandler"
      >
        <template #spinner>
          <v-progress-circular
            color="#FC7800"
            indeterminate
            size="80"
            width="10"
          />
        </template>
        <span slot="no-more" />
        <span slot="no-results" />
      </infinite-loading>
    </div>

    <div class="options">
      <v-btn
        color="var(--color-blue-800)"
        dark
        height="52"
        width="180"
        @click="showAddModal = true"
      >
        Add admin
      </v-btn>
    </div>

    <BKModal
      :is-show="showAddModal"
      title="Add admin"
      width="500"
      @update:isShow="
        (val) => {
          showAddModal = val
          if (!val) {
            newlyAddedAdmins = []
          }
        }
      "
    >
      <div class="modal-content">
        <p class="admins-title">Add admins</p>
        <BKUserSelect
          :exclude="studioAdmins.map((admin) => admin.id)"
          :select-user-chip="newlyAddedAdmins"
          label-text="Find admins to add"
          @removeUser="handleDeleteUser"
          @selectUser="handleAddUser"
        />
      </div>
      <div class="modal-options">
        <v-btn
          color="var(--color-blue-800)"
          dark
          height="52"
          width="180"
          :disabled="!newlyAddedAdmins.length || isAddingAdmin"
          @click="showAddModal = false"
        >
          <v-icon>
            {{ mdiPlus }}
          </v-icon>
          Add Admins
        </v-btn>
      </div>
    </BKModal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.admins {
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  height: 100%;

  .editor-title {
    @include font-h3;

    display: block;
    margin-bottom: 9px;
    padding: 20px 20px 0;
    font-weight: var(--font-weight-bold);

    @media (max-width: 768px) {
      @include font-body1;
    }
  }

  .search {
    padding: 0 20px 12px;
  }

  .list {
    padding: 0 20px;
    overflow: auto;
  }

  .options {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin-top: auto;
    padding: 20px;
    border-top: 1px solid var(--color-grey-600);
  }
}

.admins-title {
  margin: 12px 0;
  color: var(--color-dark-800);
}

.modal-content {
  min-height: 500px;
}

::v-deep.modal-options {
  display: flex;
  justify-content: flex-end;
  width: calc(100% + 40px);
  margin: 0 -20px;
  padding: 20px 20px 0;
  border-top: 1px solid var(--color-grey-600);
}
</style>
